import {
  useEffect,
  useState,
  isValidElement,
  cloneElement,
  useRef,
} from "react";
import { Typography } from "@mui/material";
import { catalogByPart } from "../../../apis";
import { EGBasicSelect } from "../Select/EGBasicSelect";

/**
 * getOptions: returns options for a level
 */
const getOptions = async (catalogue, isOwn, parentPath) => {
  if (!catalogue || typeof isOwn !== "boolean") {
    return [];
  }
  const resp = await catalogByPart({
    catalogue,
    is_own: isOwn,
    path: parentPath,
  });
  return resp.data.map((item) => ({
    label: item.label,
    value: item.path_locales,
  }));
};

export const InputCatalogue = ({
  label,
  defaultValue,
  catalogue,
  isOwn,
  onChange,
  wrapper,
  isValid,
}) => {
  const [value, setValue] = useState(defaultValue);

  // Only notify changes through user interaction.
  // The second argument is the selected option ({ label, value }).
  const handleChange = (newValue, option) => {
    setValue(newValue);
    onChange?.(newValue, option);
  };

  return (
    <RecursiveCatalogue
      label={label}
      catalogue={catalogue}
      isOwn={isOwn}
      parentPath={"/"}
      value={value}
      onChange={handleChange}
      wrapper={wrapper}
      defaultValue={defaultValue}
      nestNum={0}
      isValid={isValid}
    />
  );
};

const RecursiveCatalogue = ({
  label,
  catalogue,
  isOwn,
  parentPath,
  value,
  onChange,
  wrapper,
  defaultValue,
  nestNum,
  isValid,
}) => {
  const [selection, setSelection] = useState("");
  const [options, setOptions] = useState([]);
  const [error, setError] = useState(null);

  // 1) Load options for this level (parentPath)
  useEffect(() => {
    let mounted = true;
    getOptions(catalogue, isOwn, parentPath)
      .then((opts) => {
        if (!mounted) return;
        setOptions(opts || []);
        setError(null);
      })
      .catch((err) => {
        if (!mounted) return;
        console.error(err);
        setOptions([]);
        setError("Error al cargar opciones");
      });
    return () => {
      mounted = false;
    };
  }, [catalogue, isOwn, parentPath]);

  // 2) Initialize selection from defaultValue
  //    Important: only set selection if the corresponding option is already in `options`
  useEffect(() => {
    if (!defaultValue || options.length === 0) return;

    const parts = defaultValue.split("::");
    const expected = parts.slice(0, (nestNum + 1) * 2).join("::");

    // If the expected option exists in the options at this level, apply it.
    const exists = options.some((o) => o.value === expected);
    if (exists) {
      setSelection((prev) => (prev === expected ? prev : expected));
    }
    // If it does not exist, we do not set selection here; when options are updated (another effect)
    // and contains expected, the effect will run again and apply it.
  }, [defaultValue, nestNum, options]);

  // Interaction selection handling
  const handleSelection = (newValue) => {
    setSelection(newValue);
    onChange?.(newValue, options.find((o) => o.value === newValue));
  };

  const renderWithWrapper = (content) => {
    if (!wrapper) return content;

    if (isValidElement(wrapper)) {
      return cloneElement(wrapper, {}, content);
    }

    if (typeof wrapper === "function") {
      const WrapperComponent = wrapper;
      return <WrapperComponent>{content}</WrapperComponent>;
    }

    return content;
  };

  // If there are no options, we do not display anything at this level.
  if (!options || options.length === 0) return null;

  // To avoid the warning “value does not match options”:
  // we ensure that when passing `value` to the select, it is present in `options`.
  const selectionIsValid =
    selection && options.some((o) => o.value === selection);
  const selectValue = selectionIsValid ? selection : "";

  const selectContent = (
    <EGBasicSelect
      label={label ? `${label}:` : ""}
      options={options}
      value={selectValue}
      onChange={(e) => handleSelection(e.target.value)}
      isValid={selectValue ? undefined : isValid}
    />
  );

  return (
    <>
      {error && <Typography color="error">{error}</Typography>}
      {renderWithWrapper(selectContent)}
      {selectionIsValid && (
        <RecursiveCatalogue
          label={label}
          catalogue={catalogue}
          isOwn={isOwn}
          parentPath={selection}
          value={value}
          onChange={onChange}
          wrapper={wrapper}
          defaultValue={defaultValue}
          nestNum={nestNum + 1}
          isValid={isValid}
        />
      )}
    </>
  );
};
